"use client"

import { menuItems } from "@/(admin)/components/menu-items"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import AccessDenied from "@/components/custom-access-denied"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { DEVELOPER_ONLY_MODULES, formatActionName, getModuleLabel } from "@/lib/config/lib/helper"
import { LockOutlined, SaveOutlined } from "@ant-design/icons"
import { Button, Checkbox, Input, message, Space, Table, Tooltip, Typography } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useRoleUpsert } from "../hooks/useUpsertRole"
import roleService from "../services/roleService"

const { Text } = Typography

const RoleUpsertPage = () => {
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id

  const router = useRouter()
  const { createRole, updateRole, isLoading } = roleService()
  const { hasPermission: checkPermission } = useCheckPermission()

  const {
    formData,
    setFormData,
    rolePermissions,
    allPermissions,
    handlePermissionChange,
    isDefaultPermission,
  } = useRoleUpsert(id, isEditMode)

  const requiredPermission = isEditMode ? "updateRole" : "createRole"

  if (!checkPermission("role", requiredPermission)) {
    return <AccessDenied />
  }

  if (isEditMode && isLoading) {
    return <FormSkeleton mainSections={3} sidebarCards={0} />
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      message.error(t("Tên vai trò không được để trống."))
      return
    }

    // Website scope now lives on the user, not the role.
    const payload = {
      name: formData.name.trim(),
      description: formData.description,
      permissions: rolePermissions || {},
    }

    let result

    if (isEditMode) {
      result = await updateRole(id as string, payload)
    } else {
      result = await createRole(payload)
    }

    if (!result) {
      return
    }

    router.push("/admin/roles")
  }

  const isDeveloperModule = (moduleKey: string): boolean => {
    const cleanKey = moduleKey.replace("api::", "").replace("plugin::", "")
    return DEVELOPER_ONLY_MODULES.some(
      devModule => cleanKey === devModule || moduleKey === devModule
    )
  }

  const dataSource = allPermissions
  ? Object.entries(allPermissions)
      .filter(([key]) => !isDeveloperModule(key))
      .map(([key, value]: any) => ({
        key,
        moduleName: key,
        controllers: value.controllers,
      }))
  : []

  const columns = [
    {
      title: <strong>{t("Danh sách quyền")}</strong>,
      dataIndex: "moduleName",
      key: "moduleName",
      width: "30%",
      render: (text: string) => {
        const displayName = getModuleLabel(text, menuItems)
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{t(displayName)}</Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {text}
            </Text>
          </Space>
        )
      },
    },
    {
      title: <strong>{t("Hành động")}</strong>,
      key: "actions",
      render: (record: any) => {
        const controllers = record.controllers || {}
        const controllerEntries = Object.entries(controllers)
        const hasMultipleControllers = controllerEntries.length > 1

        return (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {controllerEntries.map(([controllerName, actions]: [string, any], index: number) => {
              const actionKeys = Object.keys(actions)

              const isAllChecked = actionKeys.every(action => {
                const isDefault = isDefaultPermission(record.key, controllerName, action)
                const isEnabledInState =
                  rolePermissions[record.key]?.controllers?.[controllerName]?.[action]?.enabled === true

                return isDefault || isEnabledInState
              })

              const handleSelectAllRow = (checked: boolean) => {
                actionKeys.forEach(action => {
                  if (!isDefaultPermission(record.key, controllerName, action)) {
                    handlePermissionChange(record.key, controllerName, action, checked)
                  }
                })
              }

              return (
                <div
                  key={controllerName}
                  className={`${hasMultipleControllers && index !== controllerEntries.length - 1 ? "border-b pb-4 mb-2" : ""}`}
                >
                  {hasMultipleControllers && (
                    <div className="mb-2">
                      <Text type="secondary" italic style={{ fontSize: "12px" }}>
                        {t(controllerName)}
                      </Text>
                    </div>
                  )}

                  <div className="flex items-start gap-6">
                    {actionKeys.length > 0 && (
                      <Checkbox
                        checked={isAllChecked}
                        onChange={e => handleSelectAllRow(e.target.checked)}
                        className="text-blue-600 font-bold whitespace-nowrap"
                      >
                        {t("Chọn tất cả")}
                      </Checkbox>
                    )}
                    <Space wrap size={[16, 8]}>
                      {actionKeys.map(action => {
                        return (
                          <Checkbox
                            key={action}
                            checked={
                              isDefaultPermission(record.key, controllerName, action) ||
                              !!rolePermissions[record.key]?.controllers?.[controllerName]?.[action]?.enabled
                            }
                            disabled={isDefaultPermission(record.key, controllerName, action)}
                            onChange={e => handlePermissionChange(record.key, controllerName, action, e.target.checked)}
                          >
                            <Space size={4}>
                              {formatActionName(action)}
                              {isDefaultPermission(record.key, controllerName, action) && (
                                <Tooltip title={t("Quyền mặc định hệ thống")}>
                                  <LockOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
                                </Tooltip>
                              )}
                            </Space>
                          </Checkbox>
                        )
                      })}
                    </Space>
                  </div>
                </div>
              )
            })}
          </Space>
        )
      },
    },
  ]

  const crumbs = [
    { title: t("Vai trò"), href: "/admin/roles" },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <>
      <FormSection title={t("Thông tin")} collapsible={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Text strong>{t("Tên")}*</Text>
            <Input
              className="mt-2"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <Text strong>{t("Mô tả")}</Text>
            <Input.TextArea
              className="mt-2"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title={t("Phân quyền")} collapsible={false}>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          loading={isLoading}
          rowKey="key"
          size="middle"
        />
      </FormSection>
    </>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={isLoading}
          onClick={handleSave}
        >
          {t("Lưu")}
        </Button>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? formData.name || t("Chỉnh sửa vai trò") : t("Tạo vai trò mới")}
      subtitle={formData.description || undefined}
      crumbs={crumbs}
      main={main}
      actionBar={actionBar}
    />
  )
}

export default RoleUpsertPage
